import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react'
import { MainButton } from '../../../Button/MainButton'
import { Dialog, DialogTitle, DialogContent, makeStyles, ButtonBase, MenuItem, Checkbox } from '@material-ui/core'
import { FONT_WEIGHTS } from '../../../../utils'
import BoardingWizardContext from '../BoardingWizardContext'
import useBoardingVisit from '../../../../hooks/useBoardingVisit'
import PetAvatar from '../../../Common/PetAvatar'
import { dangers, gray, purple } from '../../../../utils/colors'
import DangerConfirmationDialog from '../../../Common/Dialogs/DangerConfirmation'
import DatePicker from 'react-multi-date-picker'
import StyledMultiSelect from '../../../Common/StyledMultiSelect'
import { TIME_BLOCKS, capitilizeMultipleWords, STANDARD_DATE_FORMAT } from '../../../../utils'
import StyledAlert from '../../../Common/StyledAlert'

const useStyles = makeStyles(() => ({
	content: { display: 'flex', flexDirection: 'column', gap: '1rem' },
	header: {
		marginBottom: '.8rem'
	},
	titleText: {
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '1rem',
		textTransform: 'uppercase'
	},
	petsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		'& > div:last-child': {
			// borderBottomWidth: 0,
			// paddingBottom: 0
		}
	},
	petContainer: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		borderBottom: `1px solid ${gray[500]}`,
		paddingBottom: '1rem',
		gap: '1rem'
	},
	actionsContainer: {
		justifyContent: 'flex-start',
		marginTop: '1rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		marginTop: '1rem'
	},
	deleteButton: {
		alignSelf: 'flex-start',
		color: dangers[500],
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		letterSpacing: '0.078125rem',
		textTransform: 'uppercase',
		padding: '.5rem 0 !important'
	},
	mainActions: {
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'flex-start',
		gap: '1rem',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		}
	},
	subOptionsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.6rem',

		'& .rmdp-container': {
			width: '100%',

			'& .rmdp-week-day': {
				color: purple[500]
			},
			'& .rmdp-day.rmdp-selected span:not(.highlight)': {
				backgroundColor: purple[500],
				boxShadow: 'none'
			},
			'& .rmdp-button.rmdp-action-button': {
				color: purple[500]
			},
			'& .rmdp-arrow-container:hover': {
				backgroundColor: purple[500],
				boxShadow: 'none'
			},
			'& .rmdp-arrow-container.disabled:hover': {
				backgroundColor: 'transparent'
			},
			'& .rmdp-day:not(.rmdp-disabled):not(.rmdp-day-hidden) span:hover': {
				backgroundColor: purple[200]
			}
		}
	},
	sectionHeader: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '0.75rem',
		textTransform: 'uppercase',
		letterSpacing: '0.047rem'
	}
}))

const EditAddOnsDialog = ({
	open,
	onSave,
	onCancel,
	onDelete,
	title = 'Enrichments',
	addon_id = '',
	addon_choice_id = '',
	firstDay,
	lastDay,
	daycareFirstDay,
	daycareLastDay,
	hasEarlyDropoff,
	hasLatePickup
}) => {
	const classes = useStyles()

	const {
		wizardContextProvider: { wizard_state }
	} = useContext(BoardingWizardContext)

	const [addonsCopy, setAddonsCopy] = useState([]) // just a local copy of wizard state. Not mutated however.
	const [addOnSelectionsByPetId, setAddOnSelectionsByPetId] = useState({})
	const [availableTimeBlocks, setAvailableTimeBlocks] = useState([]) // these are the timeblocks listed as available for this addon. Will default to just midday if none listed.
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
	const { facility, updating } = useBoardingVisit()

	useEffect(() => {
		const { add_on_services } = wizard_state
		setAddonsCopy([...add_on_services])
	}, [wizard_state])

	const facilityAddons = useMemo(() => {
		if (!facility || !facility.addOnTypes) {
			return []
		}
		return facility.addOnTypes
	}, [facility])

	const addonBlockedDates = useMemo(() => {
		if (!facility) {
			return []
		}

		let blocked_dates = []

		const baseAddon = facilityAddons.find(({ _id }) => _id === addon_id)
		if (baseAddon) {
			blocked_dates = baseAddon.blocked_dates
		}

		return blocked_dates
	}, [facilityAddons, addon_id])

	const availableTimeBlocksForAddon = useMemo(() => {
		if (!facility) {
			return []
		}

		let timeBlocks = []
		let boarding_time_blocks = []

		const baseAddon = facilityAddons.find(({ _id }) => _id === addon_id)
		if (baseAddon) {
			boarding_time_blocks = baseAddon.boarding_time_blocks
		}

		timeBlocks = boarding_time_blocks.length > 0 ? boarding_time_blocks : [TIME_BLOCKS.Midday]

		return timeBlocks
	}, [facilityAddons, addon_id])

	const petsAddOnsMap = useMemo(() => {
		const { pets } = wizard_state
		const addonMap = new Map()
		const nonMealAddons = addonsCopy.filter((addon) => addon.category !== 'meal')

		const options = nonMealAddons.filter((addon) => {
			if (addon.addon_id === addon_id) {
				if (addon_choice_id > '' && addon.addon_choice_id !== addon_choice_id) {
					return false
				}
				return true
			}
			return false
		})

		for (const pet of pets) {
			const { _id } = pet
			if (!addonMap.has(_id)) {
				addonMap.set(_id, {
					petDetails: pet,
					dates: new Set(),
					timeblocks: new Set(),
					thisAddonWasAddedBeforeInvoiceScreen: false
				})
			}
		}

		options.forEach((option) => {
			const { pet_id, date, time } = option
			if (addonMap.has(pet_id)) {
				const existing = addonMap.get(pet_id)
				addonMap.set(pet_id, {
					...existing,
					dates: existing.dates.add(date),
					timeblocks: existing.timeblocks.add(time),
					thisAddonWasAddedBeforeInvoiceScreen: true
				})
			}
		})

		return addonMap
	}, [addon_id, addon_choice_id, wizard_state, addonsCopy])

	useEffect(() => {
		if (!facility) {
			setAvailableTimeBlocks([TIME_BLOCKS.Midday])
		}

		let timeBlocks = []
		let boarding_time_blocks = []

		const baseAddon = facilityAddons.find(({ _id }) => _id === addon_id)
		if (baseAddon) {
			boarding_time_blocks = baseAddon.boarding_time_blocks
		}

		timeBlocks = boarding_time_blocks.length > 0 ? boarding_time_blocks : [TIME_BLOCKS.Midday]

		setAvailableTimeBlocks(timeBlocks)
	}, [facilityAddons, addon_id, setAvailableTimeBlocks])

	useEffect(() => {
		setAddOnSelectionsByPetId((prev) => {
			const newObject = { ...prev }
			petsAddOnsMap.forEach((value, key) => {
				let fixedValues = {}
				Object.entries(value).forEach(([k, v]) => {
					if (v instanceof Set) {
						fixedValues[k] = [...v.values()]
					} else {
						fixedValues[k] = v
					}
				})

				newObject[key] = fixedValues
			})
			return newObject
		})
	}, [availableTimeBlocksForAddon, setAddOnSelectionsByPetId, petsAddOnsMap])

	const description = useMemo(() => {
		return `Please select the dates you would like to schedule ${title} for each pet listed on this appointment.`
	}, [title])

	const reconcileUpdatedAddons = useCallback(() => {
		const { _id: visitId } = wizard_state
		const { _id: facility_id } = facility

		const newAddons = addonsCopy.filter((addon) => addon.addon_id !== addon_id)

		let baseAddon = addonsCopy.find((existing) => existing.addon_id === addon_id)
		if (!baseAddon) {
			baseAddon = facilityAddons.find(({ _id }) => _id === addon_id)
		}

		const { category, name = '', type } = baseAddon

		for (const petId in addOnSelectionsByPetId) {
			const { dates, timeblocks } = addOnSelectionsByPetId[petId]

			if (dates.length === 0 || timeblocks.length === 0) {
				continue
			}

			const cleanAddon = {
				addon_id,
				pet_id: petId,
				category,
				name: name.trim(),
				type,
				visit_id: visitId,
				facility_id
			}

			if (addon_choice_id > '') {
				cleanAddon.addon_choice_id = addon_choice_id
			}

			for (const date of dates) {
				for (const timeblock of timeblocks) {
					if (
						date === firstDay &&
						(timeblock === TIME_BLOCKS.Morning || timeblock === TIME_BLOCKS.Midday)
					) {
						if (
							(timeblock === TIME_BLOCKS.Morning && daycareFirstDay && hasEarlyDropoff) ||
							(timeblock === TIME_BLOCKS.Midday && daycareFirstDay)
						) {
							newAddons.push({
								...cleanAddon,
								date,
								time: timeblock
							})
						}
					} else if (
						date === lastDay &&
						(timeblock === TIME_BLOCKS.Midday || timeblock === TIME_BLOCKS.Evening)
					) {
						if (
							(timeblock === TIME_BLOCKS.Midday && daycareLastDay) ||
							(timeblock === TIME_BLOCKS.Evening && daycareLastDay && hasLatePickup)
						) {
							newAddons.push({
								...cleanAddon,
								date,
								time: timeblock
							})
						}
					} else {
						newAddons.push({
							...cleanAddon,
							date,
							time: timeblock
						})
					}
				}
			}
		}

		return newAddons
	}, [
		addonsCopy,
		facilityAddons,
		addon_id,
		addon_choice_id,
		wizard_state,
		addOnSelectionsByPetId,
		facility,
		daycareFirstDay,
		daycareLastDay,
		hasEarlyDropoff,
		hasLatePickup
	])

	const handleCofirmDeleteClose = useCallback(() => {
		setConfirmDeleteOpen(false)
	}, [setConfirmDeleteOpen])

	const handleCofirmDelete = useCallback(() => {
		setConfirmDeleteOpen(true)
	}, [setConfirmDeleteOpen])

	const handleOnDelete = useCallback(() => {
		handleCofirmDeleteClose()
		if (onDelete) {
			onDelete()
		}
	}, [onDelete, handleCofirmDeleteClose])

	const handleOnSave = useCallback(() => {
		if (onSave) {
			const newAddons = reconcileUpdatedAddons()
			onSave(newAddons)
		}
	}, [onSave, reconcileUpdatedAddons])

	const handleOnCancel = useCallback(() => {
		if (onCancel) {
			onCancel()
		}
	}, [onCancel])

	const handleAddOnDatesChange = useCallback(
		({ dateArray, petId }) => {
			const newDates = dateArray.map((date) => date.format(STANDARD_DATE_FORMAT))
			setAddOnSelectionsByPetId((prevAddons) => {
				const newObject = { ...prevAddons }

				newObject[petId].dates = newDates
				return newObject
			})
		},
		[setAddOnSelectionsByPetId]
	)

	const handleAddOnTimeBlocksChange = useCallback(
		({ newTimeBlocksArr, petId }) => {
			setAddOnSelectionsByPetId((prevAddons) => {
				const newObject = { ...prevAddons }

				newObject[petId].timeblocks = newTimeBlocksArr
				return newObject
			})
		},
		[setAddOnSelectionsByPetId]
	)

	const getTimeBlockMenuItems = useCallback(
		(timeblocks = []) => {
			if (!availableTimeBlocks || availableTimeBlocks.length === 0) {
				return []
			}

			const menuItems = []

			availableTimeBlocks.forEach((timeBlock, i) => {
				menuItems.push(
					<MenuItem key={timeBlock} value={timeBlock}>
						<Checkbox checked={timeblocks.indexOf(timeBlock) > -1} />
						{capitilizeMultipleWords(timeBlock)}
					</MenuItem>
				)
			})
			return menuItems
		},
		[availableTimeBlocks]
	)

	const getAlertMessage = useCallback(
		({ dates = [], timeblocks = [], petName = 'Doggo', thisAddonWasAddedBeforeInvoiceScreen }) => {
			let alertMessage = ''

			if (dates.length === 0 && thisAddonWasAddedBeforeInvoiceScreen) {
				alertMessage = `No dates selected. This will remove all existing ${title} enrichments for ${petName}`
			}
			if (timeblocks.length === 0 && thisAddonWasAddedBeforeInvoiceScreen) {
				alertMessage = `No timeblocks selected. This will remove all existing ${title} enrichments for ${petName}`
			}
			if (dates.length === 0 && timeblocks.length === 0 && thisAddonWasAddedBeforeInvoiceScreen) {
				alertMessage = `No dates and timeblocks selected. This will remove all existing ${title} enrichments for ${petName}`
			}

			if (alertMessage > '') {
				return alertMessage
			}

			const datesIncludesFirstDay = dates.includes(firstDay)
			const datesIncludesLastDay = dates.includes(lastDay)
			const hasMorning = timeblocks.includes(TIME_BLOCKS.Morning)
			const hasMidday = timeblocks.includes(TIME_BLOCKS.Midday)
			const hasEvening = timeblocks.includes(TIME_BLOCKS.Evening)

			if (datesIncludesFirstDay) {
				if (daycareFirstDay && !hasEarlyDropoff && hasMorning) {
					alertMessage = `Morning enrichment not available on first day without early dropoff.`
				}
				if (!daycareFirstDay) {
					if (hasMorning && hasMidday) {
						alertMessage =
							'Morning and midday enrichments not available on first day without daycare and early dropoff.'
					} else if (hasMorning && !hasMidday) {
						alertMessage =
							'Morning enrichment not available on first day without daycare and early dropoff.'
					} else if (hasMidday && !hasMorning) {
						alertMessage = 'Midday enrichment not available on first day without daycare.'
					}
				}
			}

			if (datesIncludesLastDay) {
				if (daycareLastDay && !hasLatePickup && hasEvening) {
					alertMessage = `${alertMessage}${
						alertMessage > '' ? ' ' : ''
					}Evening enrichment not available on last day without extended pickup.`
				}
				if (!daycareLastDay) {
					if (hasEvening && hasMidday) {
						alertMessage = `${alertMessage}${
							alertMessage > '' ? ' ' : ''
						}Midday and evening enrichments not available on last day without daycare and extended pickup.`
					} else if (hasMidday && !hasEvening) {
						alertMessage = `${alertMessage}${
							alertMessage > '' ? ' ' : ''
						}Midday enrichment not available on last day without daycare.`
					} else if (hasEvening && !hasMidday) {
						alertMessage = `${alertMessage}${
							alertMessage > '' ? ' ' : ''
						}Evening enrichment not available on last day without daycare and extended pickup.`
					}
				}
			}

			if (alertMessage > '') {
				alertMessage += ` ${title} Enrichments will be scheduled for all other valid timeblocks and dates.`
			}

			return alertMessage
		},
		[firstDay, lastDay, daycareFirstDay, daycareLastDay, hasEarlyDropoff, hasLatePickup, title]
	)

	const renderCalendarDay = useCallback(
		(day) => {
			if (!day) {
				return null
			}

			if (addonBlockedDates.length < 1) {
				return null
			}
			const dateFormatted = day.date.format(STANDARD_DATE_FORMAT)
			if (addonBlockedDates.includes(dateFormatted)) {
				return { disabled: true, style: { textDecoration: 'line-through' } }
			}
		},
		[addonBlockedDates]
	)

	return (
		<>
			<DangerConfirmationDialog
				title="Delete Enrichment For All"
				description="Are you sure you want to remove this enrichment from the invoice? This will remove it for all of the pets listed on this appointment."
				open={confirmDeleteOpen}
				onConfirmDangerousAction={handleOnDelete}
				dangerButtonText="Delete"
				onCancel={handleCofirmDeleteClose}
			/>
			<Dialog
				PaperProps={{
					style: {
						width: 450,
						maxWidth: 450,
						minWidth: 450
					}
				}}
				maxWidth="md"
				open={open}
				onClose={() => null}>
				<DialogTitle>
					<div className={classes.header}>
						<div className={classes.titleText}>{`Edit ${title}`}</div>
					</div>
				</DialogTitle>
				<DialogContent className={classes.content}>
					<div>{description}</div>
					<div className={classes.petsContainer}>
						{Object.entries(addOnSelectionsByPetId).map(([petId, values], i) => {
							const {
								petDetails,
								dates: currentDates,
								timeblocks,
								thisAddonWasAddedBeforeInvoiceScreen
							} = values
							const alertMessage = getAlertMessage({
								dates: currentDates,
								timeblocks,
								petName: petDetails.name,
								thisAddonWasAddedBeforeInvoiceScreen
							})
							return (
								<div key={petDetails._id}>
									<div className={classes.petContainer}>
										<PetAvatar petName={petDetails.name} imageUrl={petDetails.image_url} />
										<div className={classes.subOptionsSection}>
											<div className={classes.sectionHeader}>Date(s)</div>
											<DatePicker
												multiple={true}
												offsetX={0}
												offsetY={0}
												zIndex={10000}
												calendarPosition={i === 0 ? 'bottom-center' : 'top-center'}
												disabled={false}
												minDate={firstDay}
												maxDate={lastDay}
												value={currentDates}
												onChange={(dateArray) => {
													handleAddOnDatesChange({ dateArray, petId })
												}}
												placeholder="Selected Dates"
												format={STANDARD_DATE_FORMAT}
												mapDays={renderCalendarDay}
												style={{
													width: '100%',
													height: 56,
													margin: 0,
													borderBottomLeftRadius: 0,
													borderBottomRightRadius: 0,
													backgroundColor: gray[100],
													border: `1px solid ${gray[500]}`,
													paddingLeft: 16,
													fontSize: '1rem',
													fontFamily: 'Nunito',
													color: gray[800]
												}}
											/>
										</div>
										<div className={classes.subOptionsSection}>
											<div className={classes.sectionHeader}>Timeblock(s)</div>
											<StyledMultiSelect
												label="Timeblocks"
												values={timeblocks}
												onValuesChanged={(newTimeBlocksArr) =>
													handleAddOnTimeBlocksChange({
														newTimeBlocksArr,
														petId
													})
												}
												menuItems={getTimeBlockMenuItems(timeblocks)}
											/>
										</div>
										{alertMessage > '' ? (
											<StyledAlert text={alertMessage} type="error" />
										) : null}
									</div>
								</div>
							)
						})}
					</div>
				</DialogContent>
				<div className={classes.actionsContainer}>
					<ButtonBase
						className={classes.deleteButton}
						disableRipple
						onClick={handleCofirmDelete}
						disabled={updating}>
						Delete Enrichment For All
					</ButtonBase>
					<div className={classes.mainActions}>
						<MainButton
							style={{ paddingTop: '6px' }}
							variant="contained"
							label="Save"
							onClick={handleOnSave}
							disabled={updating}
						/>
						<MainButton label="Cancel" onClick={handleOnCancel} disabled={updating} />
					</div>
				</div>
			</Dialog>
		</>
	)
}

export default EditAddOnsDialog
